import { CampaignValuesState, OutdatedField } from './campaignValues';

/**
 * SYM-6351. The words of the editor's messages about ads that no longer match the campaign
 * (scenes 7, 8, 10, 12; D9, D16, D25, D26, D28, M-1). Kept out of the components so the wording the
 * test book checks is tested here; the counts and "which field" come from the server.
 */

const plural = (n: number, one: string, many: string) => (n === 1 ? one : many);

/** M-1: one message names the title, the location, or both. */
export const fieldsPhrase = (fields: OutdatedField[]): string => {
  const title = fields.includes('title');
  const location = fields.includes('location');
  if (title && location) return 'job title and location';
  return title ? 'job title' : 'location';
};

/** "They show X. The campaign title is now Y." -- one sentence per changed field. */
const whatChanged = (
  fields: OutdatedField[],
  state: CampaignValuesState
): string => {
  const parts: string[] = [];
  if (fields.includes('title')) {
    const was = state.old_values.title;
    const now = state.current_values.title;
    parts.push(
      (was ? `They show "${was}". ` : '') +
        (now
          ? `The campaign title is now "${now}".`
          : 'The job title is now hidden.')
    );
  }
  if (fields.includes('location')) {
    const was = state.old_values.location;
    const now = state.current_values.location;
    parts.push(
      (was ? `They show "${was}". ` : '') +
        (now
          ? `The campaign location is now "${now}".`
          : 'The location is now hidden.')
    );
  }
  return parts.join(' ');
};

/** Scene 7, D25: live ads out of date, until the customer answers this change. */
export const liveAdsMessage = (
  state: CampaignValuesState | undefined
): {
  title: string;
  body: string;
  updateLabel: string;
  leaveLabel: string;
} | null => {
  if (!state || !state.live_ads.show_message) return null;
  const n = state.live_ads.outdated_count;
  if (n < 1) return null;
  const fields = state.live_ads.outdated_fields;

  return {
    // Mark, 28 Sep 2026: HR does not read "live" without the explanation.
    title: `${n} live ${plural(
      n,
      'ad',
      'ads'
    )} (running on social media right now) ${plural(
      n,
      'still shows',
      'still show'
    )} the old ${fieldsPhrase(fields)}`,
    body:
      whatChanged(fields, state) +
      ' If you update them, we keep a copy of how they looked and how they did until today.',
    updateLabel: `Update ${n} live ${plural(n, 'ad', 'ads')}`,
    leaveLabel: 'Leave them',
  };
};

/**
 * Scene 8 (D14, D21): the campaign's language changed and the ad texts did not. Steven, 28 Sep
 * 2026: "Show the N ads" removed (every ad has texts, so it always showed all of them); the
 * callout is named among the texts to check.
 */
export const textsLanguageMessage = (
  state: CampaignValuesState | undefined,
  campaignLanguage: string | undefined,
  adsCount: number
): {
  title: string;
  body: string;
  translateLabel: string;
} | null => {
  const old = state?.ad_texts_language;
  if (!old || adsCount < 1) return null;
  const ads = `${adsCount} ${plural(adsCount, 'ad', 'ads')}`;

  // Steven, 28 Sep 2026 (test book 11.5): a language the designs have no fixed words for is drawn
  // with English ones, so the texts go to English too -- said plainly before anything is translated.
  const toEnglish = state?.fixed_phrases?.translate_to === 'English';
  if (toEnglish && old.code.toLowerCase().startsWith('en')) return null; // already English

  if (toEnglish) {
    return {
      title: `Your ad texts are still in ${old.name}`,
      body:
        `The designs have no fixed words in ${
          campaignLanguage ?? 'this language'
        } yet, so they are shown in English. ` +
        `To keep each ad in one language, your texts will be translated to English: the text above the ad, the headline, the description and the callout of ${ads}.`,
      translateLabel: 'Translate these texts to English',
    };
  }

  return {
    title: `Your ad texts are still in ${old.name}`,
    body:
      (campaignLanguage ? `The campaign is now in ${campaignLanguage}. ` : '') +
      `Check the text above the ad, the headline, the description and the callout of ${ads}.`,
    translateLabel: 'Translate these texts',
  };
};

/** D26, D28: after an automatic restart updated ads, said once. */
export const restartNotice = (
  state: CampaignValuesState | undefined
): string | null => {
  const n = state?.restart_updated_ads_count ?? 0;
  if (n < 1) return null;
  return `${n} ${plural(
    n,
    'ad was',
    'ads were'
  )} updated when your campaign restarted`;
};

/** D25, D34: the marker on an ad that ran with an old value, and its two choices. */
export const outdatedMarker = (
  fields: OutdatedField[],
  outdatedCount: number
): { label: string; updateOne: string; updateAll: string | null } | null => {
  if (!fields.length) return null;
  const title = fields.includes('title');
  const location = fields.includes('location');

  return {
    label:
      title && location
        ? 'old title and location'
        : title
        ? 'old title'
        : 'old location',
    updateOne: 'Update this ad',
    updateAll: outdatedCount > 1 ? `Update all ${outdatedCount}` : null,
  };
};

/**
 * Scene 12 (D16, M-5, M-6): the question before a stopped campaign restarts. Null when nothing is
 * out of date, and the campaign restarts as it always has.
 */
export const restartQuestion = (
  state: CampaignValuesState | undefined
): {
  title: string;
  body: string;
  updateLabel: string;
  keepLabel: string;
  cancelLabel: string;
} | null => {
  if (!state) return null;
  const n = state.outdated_ads_count;
  if (n < 1 || !state.outdated_fields.length) return null;

  return {
    title: `${n} ${plural(
      n,
      'ad still shows',
      'ads still show'
    )} the old ${fieldsPhrase(state.outdated_fields)}`,
    body:
      whatChanged(state.outdated_fields, state) +
      ' Update them before restarting? We keep a copy of how they looked and how they did.',
    updateLabel: 'Update and restart',
    keepLabel: 'Restart as they were',
    cancelLabel: 'Cancel',
  };
};

/**
 * Mark, 28 Sep 2026: a campaign in a language the designs have no fixed words for (Finnish) shows
 * "We are hiring" and the like in English, and nothing said so.
 */
export const fixedPhrasesNotice = (
  state: CampaignValuesState | undefined
): string | null => {
  const phrases = state?.fixed_phrases;
  if (!phrases || phrases.available || !phrases.language) return null;
  return `The fixed words on the designs, such as "We are hiring", do not exist in ${phrases.language} yet, so they are shown in ${phrases.shown_in}.`;
};
